import type { CaptureResult, PostHog } from "posthog-js";
import { clearAnalyticsStorage, onConsentChange, readConsent } from "@/lib/consent/store";
import {
  allowedProperties,
  analyticsConfig,
  sanitizeUrl,
  sanitizeUrlProperties,
  type AnalyticsConfig,
  type TrackingEvent,
  type TrackingEvents,
} from "./events";

/**
 * Product analytics, only with the visitor's consent (the cookie banner,
 * `components/consent/CookieConsent.tsx`). Before a "yes", and after a "no",
 * no analytics script is loaded and nothing is stored.
 *
 * - **PostHog** (EU cloud): pageviews and the allow-listed events in
 *   `./events.ts`. No autocapture, no session recording, no heatmaps, no
 *   surveys, no feature flags, no external scripts.
 * - **Google Analytics 4** through gtag.js with Consent Mode v2: every
 *   `*_storage` starts denied; only `analytics_storage` is granted, ad storage
 *   and ad signals never are. Page views are sent by hand with a cleaned URL.
 *
 * Each runs only with its env (VITE_POSTHOG_KEY, VITE_GA_MEASUREMENT_ID) and on
 * the production hosts. Previews, tests and local builds load nothing.
 * Withdrawing consent disables both and deletes their cookies.
 */

const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";
/** The same six months the consent itself lasts. */
const COOKIE_DAYS = 182;

/** Events sent while the scripts load after a "yes"; dropped beyond this. */
const MAX_PENDING = 50;

type Gtag = (...args: unknown[]) => void;
type GtagWindow = { dataLayer?: unknown[]; gtag?: Gtag } & Record<string, unknown>;

/** `window` as gtag.js sees it: `dataLayer`, `gtag` and the `ga-disable-<id>` switch. */
function gtagWindow(): GtagWindow {
  return window as unknown as GtagWindow;
}

let config: AnalyticsConfig | null = null;
let posthog: PostHog | null = null;
let gaReady = false;
let loading = false;
let pending: [string, Record<string, string | number | boolean>][] = [];

function beforeSend(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  return {
    ...event,
    properties: sanitizeUrlProperties(event.properties) as CaptureResult["properties"],
    $set: sanitizeUrlProperties(event.$set) as CaptureResult["$set"],
    $set_once: sanitizeUrlProperties(event.$set_once) as CaptureResult["$set_once"],
  };
}

function gtag(...args: unknown[]): void {
  const w = gtagWindow();
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function () {
      // gtag.js reads the `arguments` object itself, not an array.
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
  }
  w.gtag(...args);
}

function loadGa(measurementId: string): void {
  const w = gtagWindow();
  w[`ga-disable-${measurementId}`] = false;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  gtag("consent", "update", { analytics_storage: "granted" });
  gtag("js", new Date());
  gtag("config", measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_expires: COOKIE_DAYS * 24 * 60 * 60,
    cookie_flags: "SameSite=Lax;Secure",
  });
  if (!document.querySelector(`script[src^="${GTAG_SRC}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `${GTAG_SRC}?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  }
  gaReady = true;
}

async function loadPostHog(key: string, apiHost: string): Promise<void> {
  const { default: client } = await import("posthog-js");
  client.init(key, {
    api_host: apiHost,
    persistence: "cookie",
    cookie_expiration: COOKIE_DAYS,
    cross_subdomain_cookie: true,
    secure_cookie: true,
    person_profiles: "never",
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    rageclick: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    capture_exceptions: false,
    capture_performance: false,
    disable_session_recording: true,
    disable_surveys: true,
    disable_web_experiments: true,
    advanced_disable_flags: true,
    disable_external_dependency_loading: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    mask_personal_data_properties: true,
    before_send: beforeSend,
  });
  // A previous withdrawal in this tab turned persistence off.
  client.set_config({ disable_persistence: false });
  posthog = client;
}

async function grant(): Promise<void> {
  if (!config || loading || posthog || gaReady) return;
  loading = true;
  try {
    if (config.gaMeasurementId) loadGa(config.gaMeasurementId);
    if (config.posthogKey) await loadPostHog(config.posthogKey, config.posthogHost);
  } catch {
    // Analytics must never break the app. A PostHog that failed to load still
    // leaves GA, which gets the queued events below.
  }
  // Until here every event was queued, GA ready or not: GA is set up at once but
  // PostHog is a lazy import, and an event fired in between (the sign-up form on
  // /auth?tab=register, the e-mail confirmation return) reached GA only.
  loading = false;
  const queued = pending;
  pending = [];
  pageview();
  for (const [event, properties] of queued) send(event, properties);
}

function withdraw(): void {
  pending = [];
  try {
    if (config?.gaMeasurementId) {
      gtagWindow()[`ga-disable-${config.gaMeasurementId}`] = true;
      if (gaReady) gtag("consent", "update", { analytics_storage: "denied" });
    }
    // Deletes what PostHog stored and keeps it from storing anything again.
    posthog?.set_config({ disable_persistence: true });
    posthog?.reset();
  } catch {
    // Analytics must never break the app.
  }
  posthog = null;
  gaReady = false;
  clearAnalyticsStorage();
}

/**
 * Called once at start-up (main.tsx): applies the stored choice and every
 * later one. Without config for this host, does nothing at all.
 */
export function initTracking(): void {
  if (typeof window === "undefined") return;
  config = analyticsConfig(import.meta.env, window.location.hostname);
  if (!config) return;
  const apply = (analytics: boolean) => (analytics ? void grant() : withdraw());
  const stored = readConsent();
  if (stored) apply(stored.analytics);
  onConsentChange((record) => apply(Boolean(record?.analytics)));
}

function send(event: string, properties: Record<string, string | number | boolean>): void {
  try {
    posthog?.capture(event, properties, { send_instantly: true });
    if (gaReady) gtag("event", event, properties);
  } catch {
    // Analytics must never break the app.
  }
}

/** One page view (the router calls this on every navigation). A no-op without consent. */
export function pageview(): void {
  // While loading, grant() sends the page view itself once both are ready.
  if (loading || (!posthog && !gaReady)) return;
  try {
    posthog?.capture("$pageview");
    if (gaReady) {
      gtag("event", "page_view", {
        page_location: sanitizeUrl(window.location.href),
        page_referrer: document.referrer ? sanitizeUrl(document.referrer) : undefined,
        page_title: document.title,
      });
    }
  } catch {
    // Analytics must never break the app.
  }
}

/**
 * Sends one product event. A no-op without consent or off production. Returns
 * whether it was sent (or queued while the scripts load), so a caller recording
 * "already sent" does not record an event that never left.
 */
export function track<E extends TrackingEvent>(
  event: E,
  ...[properties]: TrackingEvents[E] extends Record<string, never> ? [] : [TrackingEvents[E]]
): boolean {
  const clean = allowedProperties(event, properties as Partial<TrackingEvents[E]>);
  if (loading) {
    if (pending.length >= MAX_PENDING) return false;
    pending.push([event, clean]);
    return true;
  }
  if (posthog || gaReady) {
    send(event, clean);
    return true;
  }
  return false;
}

/** Tests only: forget the loaded clients. */
export function resetTrackingForTests(): void {
  config = null;
  posthog = null;
  gaReady = false;
  loading = false;
  pending = [];
}
